import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
  const { signInWithEmail, signInWithGoogle, signInWithMicrosoft, signUpWithEmail, isLoading } = useAuth();

  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [firmName, setFirmName] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      if (mode === 'register') {
        if (!firmName.trim()) {
          setError('Firm name is required');
          return;
        }
        await signUpWithEmail(email, password, name, firmName.trim());
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      await signInWithGoogle();
    } catch (err: any) {
      setError(err.message || 'Google sign in failed');
    }
  };

  const handleMicrosoftSignIn = async () => {
    setError('');
    try {
      await signInWithMicrosoft();
    } catch (err: any) {
      setError(err.message || 'Microsoft sign in failed');
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1>TurnKeyOS.AI</h1>
          <p>Org Board Management</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <h2>{mode === 'register' ? 'Register Your Firm' : 'Sign In'}</h2>
          {mode === 'register' && (
            <p className="register-subtitle">Start your 30-day free trial — no credit card required.</p>
          )}

          {error && <div className="error-message">{error}</div>}

          {mode === 'register' && (
            <>
              <div className="form-field">
                <label>Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  required
                />
              </div>

              <div className="form-field">
                <label>Consulting Firm Name</label>
                <input
                  type="text"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  placeholder="e.g. Acme Consulting"
                  required
                />
              </div>
            </>
          )}

          <div className="form-field">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@example.com"
              required
            />
          </div>

          <div className="form-field">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              required
              minLength={6}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading
              ? 'Loading...'
              : mode === 'register'
                ? 'Start Free Trial'
                : 'Sign In'}
          </button>

          {mode === 'login' && (
            <>
              <div className="divider">
                <span>or</span>
              </div>

              <button
                type="button"
                className="btn-google"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              <button
                type="button"
                className="btn-microsoft"
                onClick={handleMicrosoftSignIn}
                disabled={isLoading}
              >
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#F25022" d="M1 1h10v10H1z" />
                  <path fill="#00A4EF" d="M1 13h10v10H1z" />
                  <path fill="#7FBA00" d="M13 1h10v10H13z" />
                  <path fill="#FFB900" d="M13 13h10v10H13z" />
                </svg>
                Continue with Microsoft
              </button>
            </>
          )}
        </form>

        <div className="login-footer">
          {mode === 'register' ? (
            <p>
              Already have an account?{' '}
              <button onClick={() => setMode('login')}>Sign In</button>
            </p>
          ) : (
            <p>
              Own a consulting firm?{' '}
              <button onClick={() => setMode('register')}>Register for free</button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
